/**
 * officeInvoice (S1, Seth 2026-06-26) — the data layer for the OFFICE invoice
 * builder. 99.9% of invoices come from the field; this is the office fallback:
 * build + save a SERVICE invoice straight off a dispatch (no job behind it).
 *
 * Anchors on the dispatch (design: research/office-invoice-builder-design.md):
 * pegasus_job_invoice with job_id NULL + dispatch_no set = a service invoice;
 * the same record + InvoicePrintView (route /invoices/:id) renders it. Labor logs
 * to pegasus_job_labor (now dispatch-linkable) so the hours feed payroll too.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { usePlanDiscounts, type ResolvedFee } from "@/features/customer-360/serviceTypes";
import { fetchMembershipStatus } from "@/features/agreements/api";
import { repairPctForTerm } from "@/features/agreements/planPricing";
import { searchPricebook } from "@/features/items/pricebookSearch";

export interface ItemHit {
  id: string;
  description: string | null;
  model: string | null;
  part_number: string | null;
  manufacturer: string | null;
  qty_in_stock: number | null;
  base_price: number | null;
  /** The catalog category — the installer's material picker sends Refrigerant to Log the Charge. */
  category?: string | null;
}

/**
 * Smart inventory search — spans description, model, part #, manufacturer, and the
 * vendor part # (Seth 2026-06-26: "search our items / inventory list" by model
 * number, part number, manufacturer, description). Shared by the line picker, the
 * suggestion resolver, and (later) the equipment + mobile photo search.
 */
export async function searchItems(needle: string, limit = 20): Promise<ItemHit[]> {
  const n = needle.trim();
  if (n.length < 2) return [];
  // PRICEBOOK-SEARCH-01: the one pricebook search — every word, sizes ("16 x 25 x 1"),
  // part numbers with or without dashes, an exact part # / model first, inactive hidden.
  return (await searchPricebook(n, limit)) as ItemHit[];
}

/** Pricebook search for the "add from pricebook" / resolve pickers. */
export function useItemSearch(q: string) {
  return useQuery({
    queryKey: ["invoice_item_search", q],
    enabled: q.trim().length >= 2,
    staleTime: 60 * 1000,
    queryFn: () => searchItems(q, 20),
  });
}

/** A part the AI pulled from the tech notes, matched to the pricebook — PROPOSED
 *  for the office to confirm onto the invoice (never auto-added). */
export interface PartSuggestion {
  name: string;
  qty: number;
  recommended: boolean;
  reason: string;
  item_id: string | null;
  description: string; // the matched pricebook item, else the extracted name
  unitPrice: number; // the matched base_price, else 0 (office prices it)
  matched: boolean;
  /** the matched item's identifiers — shown so techs recognize the exact part
   *  (Seth 2026-08-11: "show the part numbers as well"). */
  part_number: string | null;
  model: string | null;
}

/** Code-looking tokens in free text (apr213, 25C62, L41-489, 10x20x1) — tried
 *  FIRST against the pricebook so an exact part reference beats the AI's
 *  paraphrased search (Seth 2026-08-11: "replaced apr213 filter" suggested a
 *  generic pleated filter — the code never reached the search). */
export function extractCodeTokens(s: string): string[] {
  const raw = s.match(/[A-Za-z0-9][A-Za-z0-9-]{3,}/g) ?? [];
  return raw.filter((t) => /\d/.test(t) && /[A-Za-z]/.test(t)).slice(0, 3);
}

/**
 * Suggest parts from the tech notes: Gemini (extract-parts) lists the billable
 * parts used/recommended, then we match each to the pricebook so the office can
 * pull it onto the line items + out of inventory. Propose-only.
 */
export function useSuggestParts() {
  return useMutation({
    mutationFn: async (notes: string): Promise<PartSuggestion[]> => {
      const { data, error } = await supabase.functions.invoke("extract-parts", { body: { notes } });
      if (error) throw error;
      const d = data as { ok?: boolean; parts?: { name: string; search: string; qty: number; recommended: boolean; reason: string }[] } | null;
      const parts = d?.parts ?? [];
      const out: PartSuggestion[] = [];
      for (const p of parts) {
        // Exact code tokens from the extracted text win over the AI's
        // paraphrase: a token hit only counts when the item's part#/model
        // actually contains the token (a REAL code match, not fuzzy noise).
        let m: ItemHit | null = null;
        for (const tok of extractCodeTokens(`${p.name} ${p.search}`)) {
          const hit = (await searchItems(tok, 1))[0] ?? null;
          const needle = tok.toLowerCase();
          if (
            hit &&
            ((hit.part_number ?? "").toLowerCase().includes(needle) ||
              (hit.model ?? "").toLowerCase().includes(needle))
          ) {
            m = hit;
            break;
          }
        }
        if (!m) m = (await searchItems(p.search, 1))[0] ?? null;
        out.push({
          name: p.name,
          qty: p.qty,
          recommended: p.recommended,
          reason: p.reason,
          item_id: m?.id ?? null,
          description: m?.description ?? p.name,
          unitPrice: m?.base_price != null ? Number(m.base_price) : 0,
          matched: !!m,
          part_number: m?.part_number ?? null,
          model: m?.model ?? null,
        });
      }
      return out;
    },
  });
}

/**
 * Accepted SERVICE package → pre-fill the invoice (Seth 2026-06-30). When the office
 * builds a service invoice and the customer recently accepted a Good/Better/Best
 * SERVICE proposal, its picked option's lines are offered for one-tap add — so an
 * agreed-on package isn't re-keyed. There's no hard dispatch→quote link, so we match
 * the customer's most-recent accepted service quote (last 90 days). Propose-only: the
 * office confirms (and the diagnostic line is skipped since the invoice seeds its own).
 */
export interface AcceptedServicePackage {
  quoteId: string;
  tier: string | null;
  optionLabel: string | null;
  acceptedAt: string | null;
  total: number;
  lines: { kind: string; description: string; qty: number; unitPrice: number; item_id: string | null }[];
}

export function useAcceptedServiceQuote(custno: string | null | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["accepted_service_pkg", custno, locno],
    enabled: Boolean(custno),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<AcceptedServicePackage | null> => {
      const since = new Date(Date.now() - 90 * 86400000).toISOString();
      const { data: q, error } = await supabase
        .from("pegasus_quote")
        .select("id, accepted_option_id, accepted_at")
        .eq("custno", custno as string)
        .eq("quote_type", "service")
        .eq("status", "accepted")
        .not("accepted_option_id", "is", null)
        .gte("accepted_at", since)
        .order("accepted_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!q?.accepted_option_id) return null;
      const [{ data: opt }, { data: lines }] = await Promise.all([
        supabase.from("pegasus_quote_option").select("tier, label").eq("id", q.accepted_option_id).maybeSingle(),
        supabase.from("pegasus_quote_line").select("kind, description, qty, unit_price, item_id, sort").eq("option_id", q.accepted_option_id).order("sort", { ascending: true }),
      ]);
      const mapped = ((lines ?? []) as Array<{ kind: string | null; description: string | null; qty: number | null; unit_price: number | null; item_id: string | null }>).map((l) => ({
        kind: l.kind ?? "custom",
        description: l.description ?? "",
        qty: Number(l.qty) || 1,
        unitPrice: Number(l.unit_price) || 0,
        item_id: l.item_id ?? null,
      }));
      const o = opt as { tier: string | null; label: string | null } | null;
      return {
        quoteId: q.id as string,
        tier: o?.tier ?? null,
        optionLabel: o?.label ?? null,
        acceptedAt: (q.accepted_at as string) ?? null,
        total: mapped.reduce((s, l) => s + l.qty * l.unitPrice, 0),
        lines: mapped,
      };
    },
  });
}

/**
 * Member repair discount, DERIVED from active agreements (never a manual flag —
 * [[derive_dont_flag]]). A customer is a member if they hold an active ESC agreement
 * (mirror_serv_agr, sa_inactive = 0, not expired) OR an active Pegasus-native plan
 * (pegasus_plan_sale, not cancelled, not expired). The discount % comes from the
 * plan_discount tiers keyed on the BEST (longest) plan term they hold: a 1–2 yr plan
 * gets the first tier, a 3–5 yr more, etc. (repairPctForTerm — the same source the
 * agreement card + plan builder read, so one number everywhere).
 *
 * Scope note: this is the REPAIR discount, so the office applies it to the repair work
 * (parts + labor), not the diagnostic/trip fee — see InvoiceBuilder. Easy to change in
 * one place if Sedgwick's policy is whole-ticket or waive-the-diag instead.
 */
export interface MemberDiscount {
  isMember: boolean;
  pct: number; // fraction, e.g. 0.15 for 15%
  term: number; // best plan length in years (0 = unknown)
  planLabel: string; // e.g. "PLUS member · 3-year plan"
  entryPct: number; // fraction a NON-member would get with the entry plan — for the up-sell teaser
}

export function useMemberDiscount(custno: string | null | undefined) {
  const { data: tiers } = usePlanDiscounts();
  return useQuery({
    queryKey: ["member_discount", custno, tiers?.length ?? 0],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<MemberDiscount> => {
      const today = new Date().toISOString().slice(0, 10);
      const [{ data: esc }, { data: peg }] = await Promise.all([
        supabase
          .from("mirror_serv_agr")
          .select("contr_per, expire_date")
          .eq("custno", custno!)
          .eq("sa_inactive", 0)
          .gte("expire_date", today),
        supabase
          .from("pegasus_plan_sale")
          .select("term, status, expire_date")
          .eq("custno", custno!)
          .eq("status", "sold") // MEMBER-TRUTH-01: a quote or a removed plan never earns member pricing
          .gte("expire_date", today),
      ]);
      // ESC stores term as text ("3 Year"); Pegasus as a number.
      const escTerms = ((esc ?? []) as { contr_per: string | null }[])
        .map((r) => parseInt(String(r.contr_per ?? "").replace(/[^0-9]/g, " ").trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0);
      const pegTerms = ((peg ?? []) as { term: number | null }[])
        .map((r) => Number(r.term))
        .filter((n) => Number.isFinite(n) && n > 0);
      const allTerms = [...escTerms, ...pegTerms];
      // MEMBERSHIPS-03 rule 3: a plan that has ENDED with a check still owed
      // keeps member pricing until that check is delivered.
      const life = await fetchMembershipStatus(custno!).catch(() => null);
      if (life?.member_pricing && life.term_years) allTerms.push(Number(life.term_years));
      const isMember = (esc?.length ?? 0) > 0 || (peg?.length ?? 0) > 0 || !!life?.member_pricing;
      const term = allTerms.length ? Math.max(...allTerms) : 0;
      // term || 1 → an active member with an unparseable term still gets the floor tier.
      const pct = isMember ? repairPctForTerm(tiers, term || 1) : 0;
      const entryPct = repairPctForTerm(tiers, 1); // floor tier — what a new member would get
      const planLabel = isMember ? `PLUS member${term ? ` · ${term}-year plan` : ""}` : "";
      return { isMember, pct, term, planLabel, entryPct };
    },
  });
}

/**
 * Member DIAGNOSTIC pricing (Seth 2026-06-26: "repair work [gets the %]; if there is a
 * diagnostic it would be adjusted to PLUS diagnostic pricing"). The diagnostic is NOT
 * repair-discounted — instead a member pays the separate PLUS diagnostic rate (e.g.
 * furnace/AC $109 → $79; boiler $109 → $99), straight off the `fee_item` schedule.
 *
 * We resolve the standard + plus rate for the dispatch's service type (name → fee_code →
 * resolve_fee) in the default RESIDENTIAL context. The builder only auto-applies the PLUS
 * rate when the seeded diagnostic equals that standard residential price — i.e. the
 * unambiguous residential case. Commercial / downtown / after-hours diagnostics carry a
 * different context the dispatch doesn't store, so those are left for the office to
 * confirm rather than risk mis-pricing. (Real fix later: store the fee context on the
 * dispatch at booking so the invoice can re-resolve any context precisely.)
 */
export interface MemberDiag {
  feeCode: string;
  standard: number;
  plus: number;
}

export function useMemberDiagnostic(serviceTypeName: string | null | undefined, isMember: boolean) {
  return useQuery({
    queryKey: ["member_diag", serviceTypeName, isMember],
    enabled: !!serviceTypeName && isMember,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<MemberDiag | null> => {
      const { data: st } = await supabase
        .from("service_type")
        .select("fee_code")
        .eq("name", serviceTypeName!)
        .limit(1)
        .maybeSingle();
      const feeCode = (st as { fee_code: string | null } | null)?.fee_code ?? null;
      if (!feeCode) return null;
      const [{ data: stdRows }, { data: plusRows }] = await Promise.all([
        supabase.rpc("resolve_fee", { p_fee_code: feeCode, p_segment: "residential", p_plus: false, p_downtown: false, p_after_hours: false }),
        supabase.rpc("resolve_fee", { p_fee_code: feeCode, p_segment: "residential", p_plus: true, p_downtown: false, p_after_hours: false }),
      ]);
      const std = (stdRows as ResolvedFee[] | null)?.[0]?.amount;
      const plus = (plusRows as ResolvedFee[] | null)?.[0]?.amount;
      if (std == null || plus == null) return null;
      return { feeCode, standard: Number(std), plus: Number(plus) };
    },
  });
}

export type LineKind = "fee" | "labor" | "part" | "custom";

export interface BuilderLine {
  kind: LineKind;
  description: string;
  qty: number;
  unitPrice: number;
  /** true = shown + billed on the customer invoice; false = internal-only (not shown, not in the total). */
  print: boolean;
  /** WAIVE-01: shown with the real price struck through, charged $0 — the
   *  customer SEES what was given ("diagnostic waived — replacing system"). */
  waived?: boolean;
  item_id?: string | null;
  /** PLAN-DISC-SCOPE-01: the hand choice on whether the member repair % touches this
   *  line (true / false); null = the rule decides (lib/memberPricing). Session-only today. */
  memberDiscount?: boolean | null;
}

export interface BuilderLabor {
  empno: string | null;
  name: string;
  hours: number;
  rate: number;
}

export interface SaveServiceInvoiceInput {
  /** the source dispatch, or null for a standalone invoice created straight off a customer. */
  dispatchId: string | null;
  dispatchNo: number | null;
  custno: string | null;
  locno: string | null;
  technician: string | null;
  /** The salesperson's name; undefined = leave whatever is on the invoice. */
  salesperson?: string | null;
  completedDate: string; // YYYY-MM-DD
  techNotes: string;
  internalNotes?: string;
  /** auto-derived member repair discount (its own labeled line). */
  memberDiscount?: number;
  memberDiscountLabel?: string;
  /** an additional manual/courtesy discount off the customer total. */
  discount?: number;
  /** WAIVE-01: what the manual discount is called on the invoice ("Veteran discount"). */
  discountLabel?: string;
  labor: BuilderLabor[];
  lines: BuilderLine[];
  /** mark the dispatch completed as part of saving the invoice (the office "complete").
   *  false/omitted = save a DRAFT and leave the dispatch open (come back to it later). */
  completeDispatch?: boolean;
  /** email to send the invoice to (comms-gated). Captured with the invoice. */
  recipientEmail?: string | null;
  /** also harvest recipientEmail to the customer record (pegasus_customer_email) for reuse. */
  saveEmailToRecord?: boolean;
  /** FIELD-M2: customer sign-off captured on the truck (small data-URL PNG).
   *  Omit (undefined) to leave any existing signature untouched on a re-save. */
  signatureUrl?: string | null;
  /** PO-01: vendor/PM PO or job number — prints on the invoice. Omit to leave untouched. */
  poNumber?: string | null;
  /** INVOICE-LIVE-01: the version this editor's copy is based on. 0 = "I saw no
   *  invoice"; null/undefined = don't check (a brand-new standalone invoice).
   *  A stale version is refused (InvoiceConflictError) instead of overwriting. */
  expectedVersion?: number | null;
  /** Where the save comes from — shows as "Live from Terry's iPad" / "office". */
  via?: "field" | "office";
  /** Who to credit the save to (a tech's name from the phone; defaults to the login). */
  actor?: string | null;
}

/* ── INVOICE-LIVE-01 (Seth 2026-09-24) ──────────────────────────────────────
 * ONE invoice per dispatch, shared live by the iPad and the office — no
 * "import from mobile". Every save goes through the save_invoice_draft RPC:
 * header + lines in ONE transaction (the old delete-all-then-insert could
 * leave a line-less invoice on a dropped connection), a per-dispatch lock
 * (no twin invoices from two devices), and a version check — a save built on
 * an older copy is refused with who changed it, so nobody silently writes
 * over the other ("Terry just changed this — reload"). */
export class InvoiceConflictError extends Error {
  by: string;
  via: "field" | "office" | null;
  version: number;
  constructor(by: string, via: string | null, version: number) {
    super("VERSION_CONFLICT");
    this.by = by;
    this.via = via === "field" || via === "office" ? via : null;
    this.version = version;
  }
}
export function isInvoiceConflict(e: unknown): e is InvoiceConflictError {
  return e instanceof InvoiceConflictError;
}
/** "terry@x.com" → "terry"; a tech name stays a name. */
export function saverLabel(by: string | null | undefined, via: string | null | undefined): string {
  const v = (by ?? "").trim();
  const who = v.includes("@") ? v.split("@")[0] : v;
  if (via === "field") return who ? `${who}'s iPad` : "the iPad";
  return who ? `${who} (office)` : "the office";
}

/** The lines as saved: the builder's lines + the discounts as their own
 *  labeled negative lines (so they print and flow into the total). */
export function buildSaveLines(v: Pick<SaveServiceInvoiceInput, "lines" | "memberDiscount" | "memberDiscountLabel" | "discount" | "discountLabel">): { allLines: BuilderLine[]; total: number } {
  const md = Number(v.memberDiscount) || 0;
  const disc = Number(v.discount) || 0;
  const extra: BuilderLine[] = [];
  if (md > 0) extra.push({ kind: "custom", description: v.memberDiscountLabel || "PLUS member discount", qty: 1, unitPrice: -md, print: true });
  if (disc > 0) extra.push({ kind: "custom", description: v.discountLabel?.trim() || "Discount", qty: 1, unitPrice: -disc, print: true });
  const allLines: BuilderLine[] = extra.length ? [...v.lines, ...extra] : v.lines;
  return { allLines, total: invoiceTotal(allLines) };
}

/** The one write. Returns the invoice id + its new version. */
export async function saveInvoiceRpc(a: {
  invoiceId?: string | null;
  dispatchId: string | null;
  dispatchNo: number | string | null;
  expectedVersion: number | null | undefined;
  header: Record<string, unknown>;
  lines: BuilderLine[];
  via: "field" | "office";
  actor: string | null;
}): Promise<{ id: string; version: number }> {
  const rows = a.lines.map((l) => ({
    kind: l.kind,
    description: l.description,
    qty: l.qty,
    unit_price: l.unitPrice,
    amount: l.waived ? 0 : (Number(l.qty) || 0) * (Number(l.unitPrice) || 0),
    waived: l.waived ?? false,
    print: l.print,
    // The pricebook identity survives to the row — the ESC posting bridge
    // needs it for Prod codes, COGS, and inventory movement.
    item_id: l.item_id ?? null,
  }));
  const { data, error } = await supabase.rpc("save_invoice_draft", {
    p_invoice_id: a.invoiceId ?? null,
    p_dispatch_id: a.dispatchId,
    p_dispatch_no: a.dispatchNo == null ? null : String(a.dispatchNo),
    p_expected_version: a.expectedVersion ?? null,
    p_header: a.header,
    p_lines: rows,
    p_actor: a.actor,
    p_via: a.via,
  });
  if (error) {
    if (/INSTALL_VISIT/.test(error.message ?? "")) {
      throw new Error("This is an install / new-construction visit — it bills through the job (close-out or builder draws), not a service invoice.");
    }
    if (/VERSION_CONFLICT/.test(error.message ?? "")) {
      const [ver, via] = String(error.hint ?? "").split("|");
      throw new InvoiceConflictError(String(error.details ?? ""), via ?? null, Number(ver) || 0);
    }
    throw error;
  }
  const r = data as { id: string; version: number };
  return { id: r.id, version: Number(r.version) || 0 };
}

/** Customer total = the visible (billed) lines. Hidden lines are internal reference. */
export function invoiceTotal(lines: BuilderLine[]): number {
  return lines
    .filter((l) => l.print && !l.waived)
    .reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unitPrice) || 0), 0);
}

/**
 * Save a service invoice from the builder: the invoice header + lines, the labor
 * rows (to payroll), and optionally flips the dispatch to completed. Returns the
 * new invoice id so the caller can open the print view (/invoices/:id).
 */
export function useSaveServiceInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: SaveServiceInvoiceInput): Promise<{ id: string; version: number }> => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      // Discounts ride along as their own (negative) labeled lines so they show on
      // the invoice + flow into the total naturally. The member repair discount is
      // distinct from any manual/courtesy discount, so both can appear.
      const { allLines, total } = buildSaveLines(v);
      // Completing the dispatch also finalizes the invoice (issued); a plain save keeps
      // it a draft so it can be reopened and finished later.
      const status = v.completeDispatch ? "issued" : "draft";
      const recipientEmail = (v.recipientEmail ?? "").trim() || null;

      // Round-trip: ONE dispatch = ONE service invoice. The RPC reuses the
      // dispatch's existing draft OR issued invoice instead of creating a
      // duplicate on every save — a tech who finishes, taps undo, fixes a line
      // and finishes again lands on the SAME invoice (Seth 2026-08-27). Keyed on
      // the stable dispatch_id; ESC calls (no pegasus dispatch_id) key on the
      // ESC dispatch NUMBER instead, so the truck and the office round-trip the
      // same invoice.
      const header: Record<string, unknown> = {
        dispatch_id: v.dispatchId,
        dispatch_no: v.dispatchNo == null ? null : String(v.dispatchNo),
        custno: v.custno,
        locno: v.locno,
        type: "service",
        status,
        technician: v.technician,
        completed_date: v.completedDate,
        tech_notes: v.techNotes || null,
        internal_notes: v.internalNotes || null,
        recipient_email: recipientEmail,
        total,
      };
      // Salesperson: only touch it when the caller owns it (the office picks it;
      // the phone never clears the office's pick).
      if (v.salesperson !== undefined) header.comfort_advisor = v.salesperson;
      // Signature: only touch the column when the caller sent one — a plain
      // re-save must never wipe a captured sign-off.
      if (v.signatureUrl !== undefined) header.signature_url = v.signatureUrl;
      if (v.poNumber !== undefined) header.po_number = (v.poNumber ?? "").trim() || null;

      const saved = await saveInvoiceRpc({
        dispatchId: v.dispatchId,
        dispatchNo: v.dispatchNo,
        expectedVersion: v.expectedVersion,
        header,
        lines: allLines,
        via: v.via ?? "office",
        actor: v.actor ?? email,
      });

      // Labor → payroll source (pegasus_job_labor), linked to the dispatch. Replace this
      // dispatch's office labor rows so re-saving a draft doesn't double the hours.
      // ESC calls (no dispatch_id) clean up by dispatch NUMBER, scoped to the techs
      // being written so a two-tech call never wipes the partner's rows.
      if (v.dispatchId) {
        await supabase.from("pegasus_job_labor").delete().eq("dispatch_id", v.dispatchId).eq("source", "office");
      } else if (v.dispatchNo != null) {
        const empnos = v.labor.map((l) => l.empno).filter((e): e is string => !!e);
        if (empnos.length) {
          await supabase.from("pegasus_job_labor").delete()
            .eq("dispatch_no", String(v.dispatchNo)).eq("source", "office").in("tech_empno", empnos);
        }
      }
      const realLabor = v.labor.filter((l) => (Number(l.hours) || 0) > 0);
      if (realLabor.length) {
        const { error: la } = await supabase.from("pegasus_job_labor").insert(
          realLabor.map((l) => ({
            job_id: null,
            dispatch_id: v.dispatchId,
            dispatch_no: v.dispatchNo ? String(v.dispatchNo) : null,
            tech_empno: l.empno,
            crew: l.name,
            hours: l.hours,
            rate: l.rate,
            work_date: v.completedDate,
            source: "office",
            approved: false,
            created_by: email,
          })),
        );
        if (la) throw la;
      }

      // Harvest a captured email to the customer record — Pegasus-native, since ESC is
      // read-only — so it's reusable next time and shows we now have one. Only when the
      // tech deliberately typed one in (propose→confirm; they hit save with it filled).
      if (recipientEmail && v.saveEmailToRecord) {
        try {
          await supabase.from("pegasus_customer_email").insert({
            custno: v.custno, locno: v.locno, email: recipientEmail, source: "invoice", created_by: email,
          });
        } catch (e) { console.error("email harvest failed:", e); }
      }

      // Only a real dispatch gets "completed"; a standalone invoice has none.
      if (v.completeDispatch && v.dispatchId) {
        await supabase
          .from("pegasus_dispatch")
          .update({ status: "completed", updated_at: new Date().toISOString() })
          .eq("id", v.dispatchId);
      }

      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending_ticket"] });
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["customer_email"] });
      qc.invalidateQueries({ queryKey: ["pegasus_customer_invoices"] });
      // The truck's workspace re-seeds from this — stale cache after a save read as
      // "everything I typed is gone" (Seth 2026-08-27). Never skip it.
      qc.invalidateQueries({ queryKey: ["dispatch_draft_invoice"] });
    },
  });
}

/**
 * Pegasus-native invoices for a customer (the office builder's output — service,
 * standalone, and completed-dispatch invoices), so the 360 Invoices tab can show
 * them alongside the ESC history instead of only ESC `mirror_sales`. Newest first.
 */
export interface PegasusCustomerInvoice {
  id: string;
  number: string;
  date: string | null; // ISO — completed date, else created
  type: string | null;
  status: string | null; // draft | issued
  total: number | null;
  locno: string | null;
}
export function usePegasusCustomerInvoices(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["pegasus_customer_invoices", custno],
    enabled: !!custno,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PegasusCustomerInvoice[]> => {
      const { data, error } = await supabase
        .from("pegasus_job_invoice")
        .select("id, invoice_no, dispatch_no, type, status, completed_date, created_at, total, locno, po_number")
        .eq("custno", custno as string)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
        id: r.id as string,
        number: (r.invoice_no as string) || `SH-${(r.dispatch_no as string) || String(r.id).slice(0, 6).toUpperCase()}`,
        date: (r.completed_date as string) || (r.created_at as string) || null,
        type: (r.type as string) ?? null,
        status: (r.status as string) ?? null,
        total: r.total == null ? null : Number(r.total),
        locno: (r.locno as string) ?? null,
      }));
    },
  });
}

/**
 * INV-DELETE (Seth 2026-07-02): permission-gated hard delete of a Pegasus
 * invoice via the delete_pegasus_invoice RPC — removes the header, its lines,
 * and the office-labor it created, atomically. Pre-go-live this is a true
 * delete (clearing test invoices). POST-go-live, a POSTED invoice must void
 * in ESC instead — that guard rides INV-ESC-POST.
 */
export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<void> => {
      const { data, error } = await supabase.rpc("delete_pegasus_invoice", {
        p_invoice_id: invoiceId,
      });
      if (error) throw error;
      const res = data as { ok?: boolean; error?: string } | null;
      if (!res?.ok) throw new Error(res?.error === "not_found" ? "Invoice not found" : "Delete failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pegasus_customer_invoices"] });
      qc.invalidateQueries({ queryKey: ["invoice"] });
    },
  });
}

/**
 * The customer's best email for sending the invoice: a Pegasus-harvested email first
 * (most recent — someone typed it into an invoice), else the ESC mirror_location email.
 * Drives the invoice's "email invoice to…" field: pre-filled when on file, an ask when not.
 */
export interface CustomerEmailInfo { email: string | null; onFile: boolean; source: "esc" | "pegasus" | null }
export function useCustomerEmail(custno: string | null | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["customer_email", custno, locno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CustomerEmailInfo> => {
      const { data: peg } = await supabase
        .from("pegasus_customer_email")
        .select("email")
        .eq("custno", custno!)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      const pegEmail = (peg as { email: string | null } | null)?.email?.trim() || null;
      if (pegEmail) return { email: pegEmail, onFile: true, source: "pegasus" };
      let q = supabase.from("mirror_location").select("email, email2").eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      const { data: loc } = await q.limit(1).maybeSingle();
      const l = loc as { email: string | null; email2: string | null } | null;
      const escEmail = l?.email?.trim() || l?.email2?.trim() || null;
      return { email: escEmail, onFile: !!escEmail, source: escEmail ? "esc" : null };
    },
  });
}

/**
 * FIELD-M2: the truck's view of a dispatch's existing DRAFT invoice — so a tech
 * opening the call resumes exactly what was already written (theirs or the
 * office's), never a blank duplicate. Keys on pegasus dispatch_id, else the ESC
 * dispatch number (same rule as useSaveServiceInvoice's reuse).
 */
export interface DraftInvoice {
  id: string;
  tech_notes: string | null;
  po_number: string | null;
  lines: BuilderLine[];
  /** INVOICE-LIVE-01: the version to carry into the next save + who saved last. */
  version: number;
  status: string | null;
  internal_notes: string | null;
  technician: string | null;
  comfort_advisor: string | null;
  completed_date: string | null;
  signature_url: string | null;
  updated_at: string | null;
  updated_by: string | null;
  updated_via: "field" | "office" | null;
}
export function useDispatchDraftInvoice(dispatchId: string | null, dispatchNo: number | null) {
  return useQuery({
    queryKey: ["dispatch_draft_invoice", dispatchId, dispatchNo],
    enabled: !!dispatchId || dispatchNo != null,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<DraftInvoice | null> => {
      // draft OR issued: undoing a wrap must reopen the finished write-up, not a
      // blank workspace — same one-invoice-per-dispatch rule as the save.
      let q = supabase
        .from("pegasus_job_invoice")
        .select("id, tech_notes, po_number, version, status, internal_notes, technician, comfort_advisor, completed_date, signature_url, updated_at, updated_by, updated_via")
        .in("status", ["draft", "issued"])
        .eq("type", "service")
        .order("created_at", { ascending: false })
        .limit(1);
      q = dispatchId ? q.eq("dispatch_id", dispatchId) : q.eq("dispatch_no", dispatchNo!).is("dispatch_id", null);
      const { data: inv } = await q.maybeSingle();
      if (!inv) return null;
      const { data: lines } = await supabase
        .from("pegasus_job_invoice_line")
        .select("kind, description, qty, unit_price, print, item_id, waived")
        .eq("invoice_id", (inv as { id: string }).id)
        .order("sort");
      const h = inv as Record<string, unknown>;
      return {
        id: (inv as { id: string }).id,
        tech_notes: (inv as { tech_notes: string | null }).tech_notes,
        po_number: (inv as { po_number?: string | null }).po_number ?? null,
        version: Number(h.version) || 0,
        status: (h.status as string) ?? null,
        internal_notes: (h.internal_notes as string) ?? null,
        technician: (h.technician as string) ?? null,
        comfort_advisor: (h.comfort_advisor as string) ?? null,
        completed_date: (h.completed_date as string) ?? null,
        signature_url: (h.signature_url as string) ?? null,
        updated_at: (h.updated_at as string) ?? null,
        updated_by: (h.updated_by as string) ?? null,
        updated_via: h.updated_via === "field" || h.updated_via === "office" ? h.updated_via : null,
        lines: ((lines ?? []) as Array<{ kind: string; description: string | null; qty: number | null; unit_price: number | null; print: boolean | null; item_id: string | null; waived: boolean | null }>).map((l) => ({
          kind: (l.kind as LineKind) ?? "custom",
          description: l.description ?? "",
          qty: Number(l.qty) || 1,
          unitPrice: Number(l.unit_price) || 0,
          print: l.print !== false,
          waived: l.waived === true,
          item_id: l.item_id ?? null,
        })),
      };
    },
  });
}

/** INVOICE-LIVE-01: the labor rows the invoice already carries for a dispatch
 *  (the tech's arrival-clock hours from the phone, or the office's), so the
 *  office builder opens with them instead of a blank row that would erase them. */
export function useDispatchInvoiceLabor(dispatchId: string | null) {
  return useQuery({
    queryKey: ["dispatch_draft_invoice", "labor", dispatchId],
    enabled: !!dispatchId,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<BuilderLabor[]> => {
      const { data, error } = await supabase
        .from("pegasus_job_labor")
        .select("tech_empno, crew, hours, rate")
        .eq("dispatch_id", dispatchId!)
        .eq("source", "office")
        .order("created_at");
      if (error) throw error;
      return ((data ?? []) as { tech_empno: string | null; crew: string | null; hours: number | null; rate: number | null }[])
        .map((r) => ({ empno: r.tech_empno, name: r.crew ?? "", hours: Number(r.hours) || 0, rate: Number(r.rate) || 0 }));
    },
  });
}
